import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import { canUseDracula, defaultMascotProfile, normalizeOwnedMascotProfile, type MascotProfile } from '@/components/mascot/mascotProfile';
import { advancePetCare, defaultPetCare, normalizePetCare, rewardPetCare, type CareReward, type PetAction } from '@/components/mascot/petCare';
import { defaultPetRoom, normalizePetRoom, type PetObjectKind, type PetPlacement, type PetPoint } from '@/components/mascot/petRoom';
import type { CatMood } from '@/components/mascot/petMotion';
import type { DraculaRitual } from '@/components/mascot/dracula';
import { addCareBond, addWorkRecord, addWorkTime, adoptExistingOutfit, defaultPetProgress, itemOwned, normalizePetProgress, purchasePetItems, type PetItemId } from '@/components/mascot/petProgress';
import { defaultPetTemperament, normalizePetTemperament, petRefusal, type PetIntent, type PetRefusal } from '@/components/mascot/petTemperament';

/** Питомец и его настройки принадлежат текущему аккаунту в этом браузере. */
export const useMascotStore = defineStore('mascot', () => {
  const auth = useAuthStore();
  const hasDracula = computed(() => canUseDracula(auth.user));
  const enabled = ref(true);
  const hidden = ref(false);
  const away = ref(false);
  const profile = ref(defaultMascotProfile());
  const care = ref(defaultPetCare());
  const room = ref(defaultPetRoom());
  const progress = ref(defaultPetProgress());
  const temperament = ref(defaultPetTemperament());
  const refusal = ref<{ reason: PetRefusal; at: number } | null>(null);
  let progressSnapshot: string | null = null;
  let careSnapshot: string | null = null;
  let temperamentSnapshot: string | null = null;
  const placement = ref<PetPlacement | null>(null);
  const command = ref<{ action: PetAction; pointer?: { x: number; y: number } } | null>(null);
  // Текущая игра остаётся в этой вкладке и не записывается в профиль.
  const cursorToy = ref(false);
  const roomChase = ref<{ offset: number; facingLeft: boolean; look: number } | null>(null);
  const scene = ref<'feeding' | 'playing' | 'grooming' | 'resting' | 'petting' | 'waking' | 'refusing' | 'comforting' | 'performing' | null>(null);
  const draculaRitual = ref<DraculaRitual | null>(null);
  const pose = ref<CatMood>('watching');
  const asleep = ref(false);
  const lastObjectDrag = ref({ kind: '' as string, at: 0 });
  const persistenceError = ref(false);
  const introduced = ref(false);
  const home = ref<'edge' | 'header'>('edge');
  const carrying = ref(false);
  const available = computed(() => Boolean(
    auth.user?.isActive && !auth.user.mustChangePassword,
  ));
  const visible = computed(() => available.value && enabled.value && !hidden.value && !away.value);
  type Setting = 'hidden' | 'enabled' | 'profile' | 'care' | 'room' | 'progress' | 'temperament';
  const storageKey = (setting: Setting) => `yana.mascot.${auth.user?.id}.${setting}`;

  watch(() => [auth.user?.id, auth.user?.username], () => {
    introduced.value = false;
    away.value = false;
    home.value = 'edge';
    carrying.value = false;
    profile.value = defaultMascotProfile();
    care.value = defaultPetCare();
    room.value = defaultPetRoom();
    progress.value = defaultPetProgress();
    temperament.value = defaultPetTemperament();
    refusal.value = null;
    progressSnapshot = null;
    careSnapshot = null;
    temperamentSnapshot = null;
    placement.value = null;
    command.value = null;
    cursorToy.value = false;
    roomChase.value = null;
    scene.value = null;
    draculaRitual.value = null;
    pose.value = 'watching';
    asleep.value = false;
    persistenceError.value = false;
    try {
      hidden.value = localStorage.getItem(storageKey('hidden')) === 'true';
      enabled.value = localStorage.getItem(storageKey('enabled')) !== 'false';
    } catch {
      hidden.value = false;
      enabled.value = true;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey('profile')) ?? 'null');
      // Старый личный образ Яны переезжает в отдельного героя без потери имени и заботы.
      if (hasDracula.value && saved?.cat === 'fluffy' && saved?.neckwear === 'vampire' && saved?.coat === 'black') saved.cat = 'dracula';
      profile.value = normalizeOwnedMascotProfile(saved, auth.user);
    }
    catch { /* Повреждённый профиль не сбрасывает отдельную настройку отключения. */ }
    try { careSnapshot = localStorage.getItem(storageKey('care')); care.value = normalizePetCare(JSON.parse(careSnapshot ?? 'null')); } catch { /* Значения по умолчанию. */ }
    try { room.value = normalizePetRoom(JSON.parse(localStorage.getItem(storageKey('room')) ?? 'null')); } catch { /* Комната остаётся пустой. */ }
    try {
      progressSnapshot = localStorage.getItem(storageKey('progress'));
      progress.value = normalizePetProgress(JSON.parse(progressSnapshot ?? 'null'));
      if (progressSnapshot === null && available.value) {
        progress.value = adoptExistingOutfit(progress.value, profile.value);
        save('progress', progress.value);
      }
    } catch { /* Повреждённая коллекция не выдаёт все вещи заново. */ }
    try { temperamentSnapshot = localStorage.getItem(storageKey('temperament')); temperament.value = normalizePetTemperament(JSON.parse(temperamentSnapshot ?? 'null')); } catch { /* Спокойное состояние по умолчанию. */ }
    for (const slot of ['neckwear', 'headwear'] as const) if (!itemOwned(progress.value, slot, profile.value[slot])) profile.value[slot] = 'none';
  }, { immediate: true, flush: 'sync' });

  function save(setting: Setting, value: unknown): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(storageKey(setting), serialized);
      if (setting === 'progress') progressSnapshot = serialized;
      if (setting === 'care') careSnapshot = serialized;
      if (setting === 'temperament') temperamentSnapshot = serialized;
      persistenceError.value = false;
    }
    catch { persistenceError.value = true; }
  }

  function setHidden(value: boolean): void {
    if (!available.value || !enabled.value) return;
    hidden.value = value;
    if (value) { placement.value = null; command.value = null; }
    if (!value) introduced.value = false;
    save('hidden', value);
  }

  function setEnabled(value: boolean): void {
    if (!available.value) return;
    enabled.value = value;
    if (!value) { placement.value = null; command.value = null; }
    // Включение в настройках возвращает и кошку, спрятанную кнопкой в шапке.
    if (value) setHidden(false);
    save('enabled', value);
  }

  function updateProfile(patch: Partial<MascotProfile>): void {
    if (!available.value) return;
    refreshProgress();
    if (patch.cat === 'dracula' && !hasDracula.value) return;
    const next = normalizeOwnedMascotProfile({ ...profile.value, ...patch }, auth.user);
    if (!itemOwned(progress.value, 'neckwear', next.neckwear)) next.neckwear = profile.value.neckwear;
    if (!itemOwned(progress.value, 'headwear', next.headwear)) next.headwear = profile.value.headwear;
    profile.value = next;
    save('profile', profile.value);
  }

  function resetProfile(): void { updateProfile(defaultMascotProfile()); }

  function refreshCare(): void {
    if (!available.value) return;
    try {
      const latestCare = localStorage.getItem(storageKey('care'));
      if (latestCare && latestCare !== careSnapshot) { care.value = normalizePetCare(JSON.parse(latestCare)); careSnapshot = latestCare; }
      const latestTemperament = localStorage.getItem(storageKey('temperament'));
      if (latestTemperament && latestTemperament !== temperamentSnapshot) { temperament.value = normalizePetTemperament(JSON.parse(latestTemperament)); temperamentSnapshot = latestTemperament; }
    } catch { /* Запрет хранилища не мешает заботиться в текущей вкладке. */ }
  }

  function tickCare(elapsedMs: number): void {
    if (!visible.value || document.hidden) return;
    refreshCare();
    care.value = advancePetCare(care.value, elapsedMs, asleep.value);
    temperament.value = { ...temperament.value, irritation: Math.max(0, temperament.value.irritation - Math.min(60_000, Math.max(0, elapsedMs)) / 60_000 * (asleep.value ? 12 : 5)) };
    save('care', care.value);
    save('temperament', temperament.value);
  }
  function reward(action: CareReward): boolean {
    if (!available.value || !enabled.value) return false;
    refreshCare();
    const next = rewardPetCare(care.value, action);
    if (next === care.value) return false;
    care.value = next;
    temperament.value = { ...temperament.value, irritation: Math.max(0, temperament.value.irritation - (action === 'comfort' ? 30 : 8)) };
    refusal.value = null;
    save('care', care.value);
    save('temperament', temperament.value);
    refreshProgress();
    progress.value = addCareBond(progress.value, action);
    save('progress', progress.value);
    return true;
  }
  function refreshProgress(): void {
    if (!available.value) return;
    try {
      const latest = localStorage.getItem(storageKey('progress'));
      if (latest && latest !== progressSnapshot) { progress.value = normalizePetProgress(JSON.parse(latest)); progressSnapshot = latest; }
    } catch { /* При запрете localStorage сохраняем рабочее состояние в памяти. */ }
    progress.value = normalizePetProgress(progress.value);
  }
  function recordWork(key: string): void {
    if (!available.value || !enabled.value) return;
    refreshProgress(); progress.value = addWorkRecord(progress.value, key); save('progress', progress.value);
  }
  function recordWorkTime(elapsed: number): void {
    if (!available.value || !enabled.value) return;
    refreshProgress(); progress.value = addWorkTime(progress.value, elapsed); save('progress', progress.value);
  }
  function purchase(ids: PetItemId[]): boolean {
    if (!available.value) return false;
    if (profile.value.cat === 'dracula' || !hasDracula.value && ids.includes('neckwear:vampire')) return false;
    refreshProgress();
    const next = purchasePetItems(progress.value, ids);
    if (!next) return false;
    progress.value = next; save('progress', progress.value); return true;
  }
  function refusalFor(action: PetIntent): PetRefusal | null { refreshCare(); return petRefusal(action, care.value, temperament.value, asleep.value); }
  function refuse(reason: PetRefusal): void {
    const now = Date.now();
    refusal.value = { reason, at: now };
    if (now - temperament.value.lastRefusal >= 8000) {
      temperament.value = { ...temperament.value, lastRefusal: now };
      save('temperament', temperament.value);
    }
  }
  function recordCarry(): void {
    if (asleep.value) return;
    refreshCare();
    const now = Date.now(), repeated = now - temperament.value.lastCarry < 20_000;
    temperament.value = { ...temperament.value, lastCarry: now, irritation: Math.min(100, temperament.value.irritation + (repeated ? 18 : 0)) };
    if (repeated) care.value = { ...care.value, mood: Math.max(0, care.value.mood - 6) };
    save('temperament', temperament.value); save('care', care.value);
  }
  function requestAction(action: PetAction, pointer?: { x: number; y: number }): void {
    if (!available.value || !enabled.value || away.value || carrying.value || placement.value) return;
    if (hidden.value) setHidden(false);
    command.value = { action, ...(pointer ? { pointer } : {}) };
  }
  function beginPlacement(kind: PetObjectKind, pointer?: PetPlacement['pointer']): void {
    if (!available.value || !enabled.value || away.value || carrying.value) return;
    if (hidden.value) setHidden(false);
    placement.value = { kind, pointer };
  }
  function placeObject(kind: PetObjectKind, point: PetPoint): void {
    if (!available.value || !enabled.value) return;
    room.value = normalizePetRoom({ ...room.value, [kind]: { ...point, placed: true } });
    placement.value = null;
    save('room', room.value);
  }
  function removeObject(kind: PetObjectKind): void {
    if (!available.value) return;
    room.value = { ...room.value, [kind]: { ...room.value[kind], placed: false } };
    placement.value = null;
    save('room', room.value);
  }
  function fillBowl(): void {
    if (!available.value || !enabled.value) return;
    room.value = { ...room.value, portions: 3 };
    save('room', room.value);
  }
  function eatPortion(): boolean {
    if (!available.value || !enabled.value || !room.value.bowl.placed || room.value.portions === 0) return false;
    room.value = { ...room.value, portions: room.value.portions - 1 };
    save('room', room.value);
    return true;
  }

  return { available, hasDracula, enabled, visible, away, introduced, home, carrying, profile, care, room, progress, temperament, refusal, placement, command, scene, pose, asleep, lastObjectDrag, cursorToy, roomChase, draculaRitual,
    refreshProgress, recordWork, recordWorkTime, purchase, refusalFor, refuse, recordCarry,
    persistenceError, setHidden, setEnabled, updateProfile, resetProfile, tickCare, reward, requestAction, beginPlacement, placeObject, removeObject, fillBowl, eatPortion };
});
